import { useEffect, useState } from "react";
import { arrayRemove, doc, setDoc } from "firebase/firestore";
import { X } from "lucide-react";
import { db } from "../../firebase";
import MultiSelect from "../../components/onboarding/MultiSelect";
import {
  CAREER_STAGES,
  Chips,
  JOB_TYPES,
  LOCATION_GROUPS,
  ROLE_GROUPS,
  useLocationSearchGroups,
} from "../../components/onboarding/fields";
import { MELT_LABEL } from "../../components/ui/melt";
import { cn } from "../../lib/utils";
import { rematchDreamCompanies } from "../../utils/dreamApi";
import { SETTINGS_CARD, SaveRow, SettingsHeader, useSaveState, useSettings } from "./SettingsLayout";

type JobSearchForm = { roles: string[]; jobTypes: string[]; locations: string[]; careerStage: string | null };

const strings = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);

// The answers from onboarding's "What you're looking for" and career stage.
export default function JobSearchSettings() {
  const { user, userRecord, userRecordLoading } = useSettings();
  const locationSearchGroups = useLocationSearchGroups();
  const [form, setForm] = useState<JobSearchForm | null>(null);
  const [initial, setInitial] = useState<JobSearchForm | null>(null);
  const { saving, saved, error, run } = useSaveState();

  useEffect(() => {
    if (form || userRecordLoading || !user) return;
    // careerStage was saved as an array by older onboarding versions
    const stage = userRecord?.careerStage;
    const seeded = {
      roles: strings(userRecord?.roles),
      jobTypes: strings(userRecord?.jobTypes),
      locations: strings(userRecord?.locations),
      careerStage: typeof stage === "string" ? stage : strings(stage)[0] ?? null,
    };
    setForm(seeded);
    setInitial(seeded);
  }, [form, user, userRecord, userRecordLoading]);

  if (!form || !user) {
    return <p className="font-plex text-xs text-melt-text3">Loading…</p>;
  }

  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  // Saved as soon as one is removed: separate from the form above
  const hiddenCompanies = strings(userRecord?.hiddenCompanies);
  const hiddenRoles = strings(userRecord?.hiddenRoles);
  const showAgain = (field: "hiddenCompanies" | "hiddenRoles", name: string) =>
    setDoc(doc(db, "users", user.uid), { [field]: arrayRemove(name) }, { merge: true }).catch((e) =>
      console.error("Could not show it again:", e),
    );
  const toggleJobType = (t: string) =>
    setForm({ ...form, jobTypes: form.jobTypes.includes(t) ? form.jobTypes.filter((x) => x !== t) : form.jobTypes.concat(t) });

  const save = () =>
    run(async () => {
      await setDoc(doc(db, "users", user.uid), form, { merge: true });
      setInitial(form);
      // Dream Companies' open roles are matched to these answers: redo them now, not at the next daily check
      rematchDreamCompanies();
    });

  return (
    <>
      <SettingsHeader
        title="Job search"
        description="What you're looking for. Caramel uses this to suggest roles, companies and interview prep."
      />

      <div className={cn(SETTINGS_CARD, "flex flex-col gap-6 p-6")}>
        <div className="flex flex-col gap-[9px]">
          <label htmlFor="js-roles" className={MELT_LABEL}>Roles</label>
          <MultiSelect
            id="js-roles"
            groups={ROLE_GROUPS}
            value={form.roles}
            onChange={(roles) => setForm({ ...form, roles })}
            placeholder="Search or type a role, e.g. Mechanical"
            allowCustom
          />
        </div>

        <div className="flex flex-col gap-[9px]">
          <span className={MELT_LABEL}>Type</span>
          <Chips options={JOB_TYPES} isSelected={(o) => form.jobTypes.includes(o)} onToggle={toggleJobType} />
        </div>

        <div className="flex flex-col gap-[9px]">
          <label htmlFor="js-locations" className={MELT_LABEL}>
            Locations
          </label>
          <MultiSelect
            id="js-locations"
            groups={LOCATION_GROUPS}
            searchGroups={locationSearchGroups}
            allowCustom
            value={form.locations}
            onChange={(locations) => setForm({ ...form, locations })}
            placeholder="Cities, countries, regions or Remote"
          />
        </div>

        <div className="flex flex-col gap-[9px]">
          <span className={MELT_LABEL}>Where are you in your career?</span>
          <Chips
            options={CAREER_STAGES}
            isSelected={(o) => form.careerStage === o}
            onToggle={(careerStage) => setForm({ ...form, careerStage })}
          />
        </div>

        <div className="border-t border-melt-hair pt-5">
          <SaveRow onSave={save} saving={saving} saved={saved} error={error} disabled={!dirty} />
        </div>
      </div>

      {(hiddenCompanies.length > 0 || hiddenRoles.length > 0) && (
        <div className={cn(SETTINGS_CARD, "mt-5 flex flex-col gap-5 p-6")}>
          <div>
            <span className={MELT_LABEL}>Hidden from New roles</span>
            <p className="mt-1 text-[13px] text-melt-text3">
              From “Why?” after hiding a role. Remove one to see it again.
            </p>
          </div>
          {hiddenCompanies.length > 0 && (
            <HiddenList title="Companies" names={hiddenCompanies} onShow={(name) => showAgain("hiddenCompanies", name)} />
          )}
          {hiddenRoles.length > 0 && (
            <HiddenList title="Roles, and ones like them" names={hiddenRoles} onShow={(name) => showAgain("hiddenRoles", name)} />
          )}
        </div>
      )}
    </>
  );
}

/** Hidden companies or roles, each with × to show it again */
function HiddenList({ title, names, onShow }: { title: string; names: string[]; onShow: (name: string) => void }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-[13px] font-medium text-melt-text2">{title}</span>
      <div className="flex flex-wrap gap-1.5">
        {names.map((name) => (
          <span
            key={name}
            className="inline-flex max-w-full items-center gap-1 rounded-[10px] border border-melt-hair bg-white py-1 pl-3 pr-1 text-[13px] text-melt-text1"
          >
            <span className="truncate">{name}</span>
            <button
              type="button"
              onClick={() => onShow(name)}
              aria-label={`Show ${name} again`}
              title="Show again"
              className="grid h-6 w-6 shrink-0 place-items-center rounded-[7px] text-melt-text3 transition-colors hover:bg-melt-text1/[.07] hover:text-melt-text1"
            >
              <X size={13} strokeWidth={1.75} />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}
